import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { setFlightMarkup, type MarkupType } from "../api/markups";
import { toast } from "sonner";
import { setToursMarkup } from "@/features/tours/api/ToursApi";

interface SetMarkupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  selectedIds: string[]; // id выбранных рейсов
  singleFlightPrices?: { basePrice: number; total: number } | null;
  isTours?: boolean;
}

export function SetMarkupModal({
  open,
  onOpenChange,
  selectedCount,
  selectedIds,
  singleFlightPrices,
  isTours = false,
}: SetMarkupModalProps) {
  const [markupValue, setMarkupValue] = useState("0");
  const [startPrice, setStartPrice] = useState(
    singleFlightPrices ? String(singleFlightPrices.basePrice) : ""
  );
  const [stopPrice, setStopPrice] = useState("");
  const [markupType, setMarkupType] = useState<MarkupType>("percent");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markupError, setMarkupError] = useState<string | null>(null);

  useEffect(() => {
    if (singleFlightPrices) {
      setStartPrice(String(singleFlightPrices.basePrice));
    } else {
      setMarkupValue("0");
      setStartPrice("");
      setStopPrice("");
    }
  }, [singleFlightPrices, open]);

  useEffect(() => {
    const base = parseNumber(startPrice);
    const markup = parseNumber(markupValue);

    if (base === null || markup === null) return;

    let newTotal: number;

    if (markupType === "percent") {
      newTotal = base + (base * markup) / 100;
      setStopPrice(String(Math.ceil(newTotal))); // всегда вверх для процента
    } else {
      newTotal = base + markup;
      setStopPrice(String(Math.round(newTotal))); // для абсолютной — обычное округление
    }
  }, [markupValue, markupType, startPrice]);

  const parseNumber = (val: string): number | null => {
    const cleaned = val.replace(/\s/g, "").replace(",", ".");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const numericValue = parseNumber(markupValue);
  const isFormValid =
    numericValue !== null && numericValue > 0 && selectedIds.length > 0;

  const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMarkupValue(val);

    if (val.trim() === "") {
      setMarkupError("Поле не может быть пустым");
    } else if (parseNumber(val) === null) {
      setMarkupError("Введите числовое значение");
    } else if (parseNumber(val)! <= 0) {
      setMarkupError("Значение должно быть больше нуля");
    } else {
      setMarkupError(null);
    }
  };

  // Обновлённый handleApply
  const handleApply = async () => {
    if (markupValue.trim() === "") {
      setMarkupError("Поле не может быть пустым");
      return;
    }
    if (numericValue === null) {
      setMarkupError("Введите числовое значение");
      return;
    }
    if (numericValue === null || numericValue <= 0) {
      setMarkupError("Значение должно быть больше нуля");
      return;
    }
    if (!isFormValid || isSubmitting) return;

    try {
      setIsSubmitting(true);
      if (isTours) {
        await Promise.all(
          selectedIds.map((id) =>
            setToursMarkup(+id, {
              type: markupType,
              value: numericValue!,
              apply: "replace",
            })
          )
        );
      }
      await Promise.all(
        selectedIds.map((id) =>
          setFlightMarkup(+id, {
            type: markupType,
            value: numericValue!,
            apply: "replace",
          })
        )
      );
      onOpenChange(false);
      toast.success("Наценка применена");
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Ошибка при установке наценки";
      toast.error(message);
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] w-[560px] p-6">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl font-semibold">
            Установить наценку
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Новая наценка заменит текущую для {selectedCount}-х выбранных рейсов
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {/* Значение наценки */}
          <div className="space-y-2">
            <label className="block text-sm font-normal text-gray-900">
              Введите значение
            </label>
            <div className="relative">
              <Input
                type="text"
                value={markupValue}
                onChange={handleMarkupChange}
                className={`h-10 text-base w-full ${
                  markupError ? "border-red-500 focus-visible:ring-red-500" : ""
                }`}
                style={{ paddingRight: "2.5rem" }}
              />
              <div
                className="text-base text-gray-500"
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                {markupType === "percent" ? "%" : "₸"}
              </div>
            </div>
            {markupError && (
              <p className="text-sm text-red-500">{markupError}</p>
            )}
          </div>

          {/* Диапазон цен — только UI-подсказка, не уходит в API */}
          {selectedIds.length === 1 && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-normal text-gray-900">
                  Было
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    value={startPrice}
                    onChange={(e) => setStartPrice(e.target.value)}
                    className="h-10 text-base w-full"
                    style={{ paddingRight: "2.5rem" }}
                  />
                  <div
                    className="text-base text-gray-500"
                    style={{
                      position: "absolute",
                      right: "1rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    ₸
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-normal text-gray-900">
                  Стало
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    value={stopPrice}
                    onChange={(e) => setStopPrice(e.target.value)}
                    className="h-10 text-base w-full"
                    style={{ paddingRight: "2.5rem" }}
                  />
                  <div
                    className="text-base text-gray-500"
                    style={{
                      position: "absolute",
                      right: "1rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    ₸
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Select
              value={markupType}
              onValueChange={(val: MarkupType) => setMarkupType(val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Тип наценки" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Процентная</SelectItem>
                <SelectItem value="absolute">Абсолютная</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Предупреждения */}
          <div className="space-y-1.5 text-sm">
            <div
              className="flex items-start gap-2"
              style={{ color: "#e11d48" }}
            >
              <span className="select-none mt-0.5">•</span>
              <span>Вылет доступен только для абсолютной наценки</span>
            </div>
          </div>
        </div>

        {/* Кнопки */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleApply}
            className="flex-1 h-10"
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? "Применяем..." : "Применить наценку"}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-10"
          >
            Отмена
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
